import { useColorMode, useColors } from '../../../theme/ColorModeContext';

/**
 * Series colours for Intel charts, in fixed order: series 1 is always the brand
 * red, series 2 always the purple. The dark steps sit a little deeper than the
 * UI accents: the accents are tuned for text on #08080e and fall outside the
 * lightness band a filled chart mark needs. Checked with the dataviz palette
 * validator (lightness, chroma, colour-blind separation, contrast) in both modes.
 */
const SERIES = {
    light: ['#b91c1c', '#5b21b6'],
    dark: ['#e85555', '#a97bd6'],
};

export const useChartTheme = () => {
    const colors = useColors();
    const { mode } = useColorMode();
    const series = SERIES[mode === 'dark' ? 'dark' : 'light'];
    return {
        series,
        axis: {
            tick: { fill: colors.text.muted, fontSize: 11, fontFamily: '"Share Tech Mono", monospace' },
            axisLine: { stroke: colors.border.default },
            tickLine: false,
        },
        grid: { stroke: colors.border.subtle, vertical: false },
        tooltip: {
            contentStyle: {
                background: colors.bg.primary,
                border: `1px solid ${colors.border.default}`,
                borderRadius: 6,
                fontSize: 12,
                color: colors.text.primary,
            },
            labelStyle: { color: colors.text.secondary, fontFamily: '"Share Tech Mono", monospace' },
            itemStyle: { color: colors.text.primary },
            cursor: { stroke: colors.border.strong, fill: colors.accent.cyanGlow },
        },
        legend: { wrapperStyle: { fontSize: 12, color: colors.text.secondary } },
    };
};

/** `#rrggbb` at an opacity: the sequential scale for heat cells, one hue. */
export const withAlpha = (hex: string, alpha: number): string => {
    const value = hex.replace('#', '');
    const r = parseInt(value.slice(0, 2), 16);
    const g = parseInt(value.slice(2, 4), 16);
    const b = parseInt(value.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha)).toFixed(3)})`;
};
